import * as S from './styles';
import { CSSProperties, useTheme } from 'styled-components';
import Text from '@/components/atoms/Text';
import Avatar from '@/components/atoms/Avatar';
import Button from '@/components/atoms/Button';
import { IconeCurtir } from '@/components/atoms/Icones/IconeCurtir';
import { IconeChippu } from '@/components/atoms/Icones/IconeChippu';
import useBreakpoint from '@/hooks/useBreakpoint';
import obterIniciaisNomeUsuario from '@/utils/obterIniciaisNomeUsuario';
import { useMemo, useState } from 'react';
import { registrarEvento } from '@/services/googleAnalytics';
import { useSession } from 'next-auth/react';
import { useAuthenticationModal } from '@/hooks/useAuthenticationModal';

const CHARACTER_LIMIT = 300;

interface ResenhaComentarioProps {
  $nomeFicha: string;
  $id: string;
  $nota: number;
  $qtdCurtidas: number;
  $onClickCurtir: (id: string, curtir: boolean) => void;
  $onClickDenunciar: (id: string) => void;
  $nome: string;
  $imagemPerfil?: string;
  $comentario?: string;
  $timeStamp?: string;
  $curtida?: boolean;
  usuarioEstaLogado: boolean;
  style?: CSSProperties;
}

export default function ResenhaComentario(props: ResenhaComentarioProps) {
  const {
    $nomeFicha,
    $id,
    $nota,
    $qtdCurtidas,
    $onClickCurtir,
    $onClickDenunciar,
    $nome,
    $imagemPerfil,
    $comentario,
    $timeStamp,
    $curtida,
    usuarioEstaLogado,
    style,
  } = props;

  const { breakpoint } = useBreakpoint();
  const theme = useTheme();

  const [curtidaPorMim, setCurtidaPorMim] = useState($curtida);
  const [qtdCurtidas, setQtdCurtidas] = useState($qtdCurtidas);
  const [verMais, setVerMais] = useState(false);

  const isTextLong = ($comentario?.length || 0) > CHARACTER_LIMIT;

  const { status } = useSession();
  const { mostrarAuthenticationModalContext } = useAuthenticationModal();

  const comentarioTratado = useMemo(() => {
    if (breakpoint === 'desktop') return $comentario;

    return !verMais && isTextLong
      ? $comentario?.slice(0, CHARACTER_LIMIT) + '...'
      : $comentario;
  }, [breakpoint, verMais, $comentario, isTextLong]);

  function curtir(id: string) {
    if (status !== 'authenticated') return mostrarAuthenticationModalContext();

    const curtidaTemp = !curtidaPorMim;

    registrarEvento('clique_curtir_resenha', {
      ficha: $nomeFicha,
      resenhaID: $id,
      curtir: curtidaTemp,
    });

    $onClickCurtir?.(id, curtidaTemp);

    setCurtidaPorMim(curtidaTemp);
    setQtdCurtidas((prevState) =>
      curtidaTemp ? prevState + 1 : prevState - 1,
    );
  }

  return (
    <S.Container style={style}>
      <S.Row
        style={{
          alignItems: 'center',
          gap: theme.spacing[breakpoint === 'mobile' ? 3 : 5],
          width: '100%',
        }}
      >
        <Avatar
          $size={breakpoint === 'mobile' ? 3 : 4}
          $initials={obterIniciaisNomeUsuario($nome ?? '')}
          $shape="rounded"
          $variant="solid"
          $color="accent"
          $preencherFundo={!$imagemPerfil}
          $initialsColor={theme.tokens.colors.black_contrast}
          $image={
            $imagemPerfil !== '' && $imagemPerfil != null
              ? $imagemPerfil
              : undefined
          }
        />
        <Text
          $size={2}
          $color={theme.colors.accent.accent[9]}
          $weight="semibold"
          $align="center"
          $highContrast={false}
          $text={$nome}
        />
        <Text
          $size={2}
          $color="neutral"
          $weight="regular"
          $align="center"
          $highContrast={true}
          $text={$timeStamp}
        />
      </S.Row>

      <S.Container
        style={{
          gap: theme.spacing[5],
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        <Text
          $size={2}
          $color="neutral"
          $weight="regular"
          $align="left"
          $highContrast
          $text={comentarioTratado}
          $quebraTexto
        />
        {breakpoint === 'mobile' && isTextLong ? (
          <Button
            $size={2}
            $variant="outline"
            $focusRing
            $highContrast={true}
            $color="neutral"
            $loading={false}
            $text={verMais ? '- Ler menos' : '+ Ler mais'}
            style={{ width: '100%', justifyContent: 'center' }}
            $onClick={() => setVerMais((prevState) => !prevState)}
          />
        ) : null}
      </S.Container>
      <S.Row
        style={{
          width: '100%',
          gap: theme.spacing[5],
        }}
      >
        <S.Row style={{ gap: theme.spacing[2] }}>
          <IconeChippu size={18} color={theme.colors.accent.accent[9]} />
          <Text
            $size={2}
            $color={''}
            $weight="bold"
            $align="center"
            $highContrast={true}
            $text={`${$nota}/5`}
          />
        </S.Row>
        <S.Botao style={{ gap: theme.spacing[2] }} onClick={() => curtir($id)}>
          <IconeCurtir
            size={18}
            color={curtidaPorMim ? theme.colors.accent.accent[9] : 'white'}
          />
          <Text
            $size={2}
            $color={''}
            $weight="bold"
            $align="center"
            $highContrast={true}
            $text={qtdCurtidas}
          />
        </S.Botao>
        {usuarioEstaLogado ? (
          <Button
            $size={breakpoint === 'mobile' ? 2 : 3}
            $variant="soft"
            $focusRing
            $highContrast={false}
            $color="neutral"
            $loading={false}
            $text="Denunciar"
            $onClick={() => $onClickDenunciar($id)}
          />
        ) : null}
      </S.Row>
    </S.Container>
  );
}
